import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { ImageUp as ImageUpload, Upload, X, ArrowUpDown, ChevronLeft, ChevronRight, ChevronDown, Camera, ImageIcon, AlertCircle, CheckCircle, Loader2, HelpCircle } from 'lucide-react';
import { ImageFile, Scene } from '../types';
import { ValidationResult, ValidationState } from '../types/validation';
import { validateAllImages, canReconstruct, getValidationSummary, processImagesWithQuality, validateImageCount } from '../utils/imageValidation';
import { DEFAULT_VALIDATION_CONFIG } from '../types/validation';
import { DetailedValidationSidebar } from './DetailedValidationSidebar';
import { hasAnyExifData } from '../utils/exifReader';
import { getFileType } from '../utils/fileTypeDetection';
import logo from '../assets/logo.gif'; 

interface Props {
  onImagesSelected: (images: ImageFile[]) => void;
  onImageClick: (image: ImageFile) => void;
  images: ImageFile[];
  captureType: Scene['captureType'];
  onValidationChange?: (canReconstruct: boolean, results?: ValidationResult[]) => void;
  onValidationStart?: () => void;
}

// Helper function to generate camera IDs dynamically
const generateCameraIds = (count: number): string[] => {
  return Array.from({ length: count }, (_, i) => `camera${i + 1}`);
};

// Helper function to get camera label
const getCameraLabel = (cameraId: string): string => {
  if (cameraId === 'all') return 'All Cameras';
  const num = parseInt(cameraId.replace('camera', ''));
  return `Cam ${num}`;  // Simple and concise
};

// Helper function to normalize captureType to number
const getCameraCount = (captureType: Scene['captureType']): number => {
  if (typeof captureType === 'number') return captureType;
  if (captureType === 'single') return 1;
  if (captureType === 'dual') return 2;
  if (captureType === 'triple') return 3;
  if (captureType === 'quad') return 4;
  return 1; // default
};

type CameraId = string; // Can be 'camera1', 'camera2', ... 'camera20', or 'all'


export default function ImageUploader({ onImagesSelected, onImageClick, images, captureType, onValidationChange,onValidationStart }: Props) {
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadVisible, setIsUploadVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CameraId>('camera1');
  const [dragDropCamera, setDragDropCamera] = useState<CameraId>('camera1');

  //debug block
  useEffect(() => {
  console.log('CAMERA STATE CHANGED:', {
    selectedCamera,
    dragDropCamera,
    timestamp: new Date().toISOString()
  });
}, [selectedCamera, dragDropCamera]);
  
  // Validation state
  const [validationState, setValidationState] = useState<ValidationState>({
    status: 'idle',
    results: [],
    canReconstruct: false,
    progress: 0,
    currentStep: 'Ready'
  });
  
  // Use useRef to maintain a persistent counter across re-renders
  const idCounterRef = useRef(0);
  
  const imagesPerPage = 50;

  const validationInProgressRef = useRef(false);

  


  // Reset upload visibility when all images are deleted
  useEffect(() => {
    if (images.length === 0 && !isUploadVisible) {
      setIsUploadVisible(true);
    }
  }, [images.length, isUploadVisible]);


  // Validation Dashboard Component
  const ValidationDashboard = () => {
    if (validationState.status === 'idle') return null;
    
    const summary = getValidationSummary(validationState.results);
    
    return (
      <div className="p-4 bg-[#161513] rounded-lg border border-[#0CB4E4]/30 mb-6 shadow-md">
        <div className="text-sm font-medium text-gray-300 mb-3">Dataset Validation</div>
        
        {validationState.status === 'validating' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#0CB4E4]" />
              <span className="text-sm text-gray-300">{validationState.currentStep}</span>
            </div>
            <div className="w-full bg-[#161513] rounded-full h-2">
              <div
                className="h-2 rounded-full bg-[#0C5484] transition-all duration-300"
                style={{ width: `${validationState.progress}%` }}
              />
            </div>
            <div className="text-xs text-gray-400">
              Analyzing {images.length} images... {validationState.progress}%
            </div>
          </div>
        )}
        
        {validationState.status === 'complete' && (
          <div>
            {summary.canProceed ? (
              <div className="flex items-center gap-3 text-white">
                <CheckCircle className="w-5 h-5" />
                <div>
                  {validationState.currentStep === 'No EXIF data detected - validation skipped' ? (
                    <div>
                      <div className="font-medium text-white">Dataset ready for reconstruction!</div>
                      <div className="text-sm text-[#0CB4E4] mt-1">
                        ⚡ No EXIF data detected - validation skipped. Images processed as raw dataset.
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="font-medium text-white">Dataset ready for reconstruction!</div>
                      <div className="text-sm text-white mt-1">
                        All {images.length} images passed validation requirements
                        {summary.warningCount > 0 && (
                          <span className="text-[#0CB4E4] ml-2">
                            ({summary.warningCount} warning{summary.warningCount > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 text-[#0CB4E4] mb-4">
                  <AlertCircle className="w-5 h-5" />
                  <div>
                    <div className="font-medium">
                      Found {summary.blockerCount} blocking issue{summary.blockerCount > 1 ? 's' : ''} 
                      {summary.warningCount > 0 && ` and ${summary.warningCount} warning${summary.warningCount > 1 ? 's' : ''}`}
                    </div>
                    <div className="text-sm text-[#0CB4E4] mt-1">Please fix the following before triggering the reconstruction</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {summary.blockers.map((result, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#0CB4E4]/10 rounded-lg border border-[#0CB4E4]/30">
                      <AlertCircle className="w-4 h-4 text-[#0CB4E4] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#0CB4E4]">{result.message}</div>
                        {result.details && (
                          <div className="text-xs text-[#0CB4E4] mt-1">{result.details}</div>
                        )}
                        {result.affectedImages.length > 0 && (
                          <div className="text-xs text-[#0CB4E4] mt-1">
                            Affects {result.affectedImages.length} image(s)
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {summary.warnings.map((result, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#0CB4E4]/10 rounded-lg border border-[#0CB4E4]/30">
                      <AlertCircle className="w-4 h-4 text-[#0CB4E4] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#0CB4E4]">{result.message}</div>
                        {result.details && (
                          <div className="text-xs text-[#0CB4E4]/80 mt-1">{result.details}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-4 bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-[#0CB4E4] mt-0.5" />
                    <div className="text-sm text-[#0CB4E4]">
                      <strong>Action required:</strong> Remove or replace the problematic images and try again. 
                      Each image should be in landscape orientation with consistent resolution per camera.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Set up camera tabs based on captureType
  const cameraTabs = useMemo(() => {
    const count = getCameraCount(captureType);
    return generateCameraIds(count);
  }, [captureType]);

  // Add "All Cameras" option if more than one camera
  const cameraOptions = useMemo(() => {
    if (cameraTabs.length > 1) {
      return [...cameraTabs, 'all'];
    }
    return cameraTabs;
  }, [cameraTabs]);

  // Reset to first camera when capture type changes
  useEffect(() => {
    setSelectedCamera('camera1');
    setDragDropCamera('camera1');
    setSelectedImages(new Set());
    setCurrentPage(1);
  }, [captureType]);

  // Run validation whenever images change (but prevent infinite loops)
  useEffect(() => {
  if (images.length === 0) {
    setValidationState({
      status: 'idle',
      results: [],
      canReconstruct: false,
      progress: 0,
      currentStep: ''
    });
    onValidationChange?.(false, []);
    return;
  }
  
  if (validationInProgressRef.current) {
    return;
  }
  
  validationInProgressRef.current = true;
  onValidationStart?.();
  
  setValidationState(prev => ({ 
    ...prev, 
    status: 'validating',
    progress: 0,
    currentStep: 'Starting validation...'
  }));
  
  const runValidation = async () => {
    try {
      console.log('🔍 [ImageUploader] Starting per-camera validation');
      
      // group images by camera
     const imagesByCamera: Record<string, ImageFile[]> = {};
      images.forEach(img => {
        const cameraId = img.cameraId || 'camera1';
        if (!imagesByCamera[cameraId]) {
          imagesByCamera[cameraId] = [];
        }
        imagesByCamera[cameraId].push(img);
      });
      
      console.log('Cameras:', Object.keys(imagesByCamera).map(cam => 
        `${cam}=${imagesByCamera[cam].length} images`
      ).join(', '));
      
      // Validate each camera independently
      let allResults: any[] = [];
      let allProcessedImages: ImageFile[] = [];
      let totalProgress = 0;
      const cameraCount = Object.keys(imagesByCamera).length;
      
      for (const [cameraId, cameraImages] of Object.entries(imagesByCamera) as [string, ImageFile[]][]) {
        console.log(`Validating ${cameraId}: ${cameraImages.length} images`);
        
        const { results, processedImages } = await validateAllImages(
          cameraImages,  // this is ImageFile[]
          undefined,
          (progress, step) => {
            const overallProgress = Math.floor(totalProgress + (progress / cameraCount));
            setValidationState(prev => ({
              ...prev,
              progress: overallProgress,
              currentStep: `${step} (${cameraId})`
            }));
          }
        );
        
        // Tag results with camera
        const cameraTaggedResults = results.map(r => ({
          ...r,
          cameraId,
          affectedImages: r.affectedImages
        }));
        
        allResults = [...allResults, ...cameraTaggedResults];
        allProcessedImages = [...allProcessedImages, ...processedImages];
        totalProgress += (100 / cameraCount);
      }
      
      console.log('✅ Validation complete:', allResults.length, 'results');
      
      const canProceed = canReconstruct(allResults);
      
      // Update images with validation results
      const updatedImages = allProcessedImages.map(img => ({
        ...img,
        validationErrors: allResults.filter(r => r.affectedImages.includes(img.id))
      }));
      
      // Check for changes
      const hasChanges = updatedImages.some((img, index) => {
        const originalImg = images[index];
        if (!originalImg) return true;
        
        const originalErrors = originalImg.validationErrors || [];
        const newErrors = img.validationErrors || [];
        const errorsChanged = JSON.stringify(originalErrors) !== JSON.stringify(newErrors);
        
        const fileChanged = originalImg.file.name !== img.file.name || 
                           originalImg.file.type !== img.file.type;
        
        return errorsChanged || fileChanged;
      });
      
      if (hasChanges) {
        console.log('🔄 Updating images with validation results');
        onImagesSelected(updatedImages);
      }
      
      setValidationState({
        status: 'complete',
        results: allResults,
        canReconstruct: canProceed,
        progress: 100,
        currentStep: 'Validation complete'
      });
      
      onValidationChange?.(canProceed, allResults);
      
    } catch (error) {
      console.error('❌ Validation error:', error);
      const errorResult = {
        isValid: false,
        category: 'blocker' as const,
        rule: 'validation-failed',
        message: 'Validation process failed',
        affectedImages: [],
        details: error instanceof Error ? error.message : 'Unknown error'
      };
      
      setValidationState({
        status: 'complete',
        results: [errorResult],
        canReconstruct: false,
        progress: 100,
        currentStep: 'Validation failed'
      });
      
      onValidationChange?.(false, [errorResult]);
      
    } finally {
      validationInProgressRef.current = false;
    }
  };
  
  const timer = setTimeout(runValidation, 500);
  
  return () => {
    clearTimeout(timer);
    validationInProgressRef.current = false;
  };
  
}, [images.length]);

  //finish validation 

  const cameraImages = useMemo(() => {
    if (selectedCamera === 'all') {
      return images;
    }
    return images.filter(img => img.cameraId === selectedCamera);
  }, [images, selectedCamera]);

  // Check CURRENT CAMERA's file type, not global + check all images not just first
  const isExrDataset = cameraImages.length > 0 && 
                     getFileType(cameraImages[0].file) === 'exr' &&
                     cameraImages.every(img => getFileType(img.file) === 'exr');

  // Sort images based on validation status (errors first)
  const sortedImages = useMemo(() => {
    return [...cameraImages].sort((a, b) => {
      const aHasErrors = (a.validationErrors?.length || 0) > 0;
      const bHasErrors = (b.validationErrors?.length || 0) > 0;
      
      // Errors first, then by upload order
      if (aHasErrors && !bHasErrors) return -1;
      if (!aHasErrors && bHasErrors) return 1;
      return 0;
    });
  }, [cameraImages]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedImages.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const paginatedImages = sortedImages.slice(startIndex, startIndex + imagesPerPage);

  const toggleImageSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedImages.size === 0) return;
    
    const message = selectedImages.size === cameraImages.length
      ? 'Are you sure you want to delete all images?'
      : `Are you sure you want to delete ${selectedImages.size} selected image${selectedImages.size > 1 ? 's' : ''}?`;

    if (window.confirm(message)) {
      const updatedImages = images.filter(img => !selectedImages.has(img.id));
      onImagesSelected(updatedImages);
      setSelectedImages(new Set());
    }
  };

  const toggleSelectAll = () => {
    if (selectedImages.size === cameraImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(cameraImages.map(img => img.id)));
    }
  };

  const handleCameraChange = (camera: CameraId) => {
    setSelectedCamera(camera);
    setSelectedImages(new Set());
    setCurrentPage(1);
  };

  const handleDragDropCameraChange = (camera: CameraId) => {
    console.log('🔄 handleDragDropCameraChange called:', {
    oldDragDropCamera: dragDropCamera,
    oldSelectedCamera: selectedCamera,
    newCamera: camera
  });
  setDragDropCamera(camera);
  setSelectedCamera(camera);  // Also update which camera is displayed
  setSelectedImages(new Set());  // Clear selection when switching cameras
  setCurrentPage(1);  // Reset to first page
};

  // Generate a truly unique ID using crypto API and ref counter
  const generateUniqueId = useCallback((fileName: string): string => {
    idCounterRef.current += 1;
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    
    return `${dragDropCamera}-${fileName.replace(/[^a-zA-Z0-9.-]/g, '_')}-${timestamp}-${idCounterRef.current}-${random}`;
  }, [dragDropCamera]);

  const createNewImages = useCallback((files: File[]): ImageFile[] => {
  console.log('📸 createNewImages called:', {
    dragDropCamera,
    fileCount: files.length,
    fileNames: files.map(f => f.name)
  });
  
  const newImages = files.map((file, index) => {
    const img = {
      id: generateUniqueId(file.name + '-' + index),
      file,
      preview: URL.createObjectURL(file),
      cameraId: dragDropCamera,  // ⭐ Check this value in console
      quality: null,
      validationErrors: []
    };
    
    console.log(`📸 Created image ${index + 1}:`, {
      fileName: file.name,
      cameraId: img.cameraId,
      id: img.id
    });
    
    return img;
  });
  
  return newImages;
}, [dragDropCamera, generateUniqueId]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      
      if (dragDropCamera === 'all') {
        return;
      }
      
      const newFiles = Array.from(e.dataTransfer.files).filter(file => 
        file.type.startsWith('image/')
      );
      
      if (newFiles.length === 0) {
        return;
      }
      
      const newImages = createNewImages(newFiles);
      onImagesSelected([...images, ...newImages]);
      setIsUploadVisible(false);
    },
    [images, onImagesSelected, dragDropCamera, createNewImages]
  );


const handleFileSelect = useCallback(
  (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('📂 handleFileSelect triggered:', {
      dragDropCamera,
      selectedCamera
    });
    
    if (dragDropCamera === 'all') {
      console.log('❌ Cannot upload to "all" cameras');
      return;
    }
    
    const newFiles = Array.from(e.target.files || []);
    
    if (newFiles.length === 0) {
      console.log('❌ No files selected');
      return;
    }
    
    console.log('📂 Files selected:', newFiles.length, 'files');
    const newImages = createNewImages(newFiles);
    
    console.log('📂 New images created:', {
      count: newImages.length,
      cameraIds: newImages.map(img => img.cameraId),
      uniqueCameraIds: [...new Set(newImages.map(img => img.cameraId))]
    });
    
    onImagesSelected([...images, ...newImages]);
    setIsUploadVisible(false);
    
    e.target.value = '';
  },
  [images, onImagesSelected, dragDropCamera, selectedCamera, createNewImages]
);

  const removeImage = (id: string) => {
    onImagesSelected(images.filter(img => img.id !== id));
  };

const getDragDropText = () => {
    if (dragDropCamera === 'all') {
      return 'Select a specific camera to upload images';
    } else {
      return `Browse files for ${getCameraLabel(dragDropCamera)} images`;
    }
  };

  return (
    <div className="w-full relative">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>
      
      <div className="relative z-10">
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isUploadVisible ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
          {cameraTabs.length > 1 && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="w-full">
                  <style>{`
                    .camera-tabs-scroll {
                      scrollbar-width: thin;
                      scrollbar-color: rgba(8, 121, 163, 0.5) rgba(8, 121, 163, 0.1);
                    }
                    .camera-tabs-scroll::-webkit-scrollbar {
                      height: 8px;
                    }
                    .camera-tabs-scroll::-webkit-scrollbar-track {
                      background: rgba(8, 121, 163, 0.1);
                      border-radius: 4px;
                    }
                    .camera-tabs-scroll::-webkit-scrollbar-thumb {
                      background: rgba(8, 121, 163, 0.5);
                      border-radius: 4px;
                    }
                    .camera-tabs-scroll::-webkit-scrollbar-thumb:hover {
                      background: rgba(8, 121, 163, 0.7);
                    }
                  `}</style>
                  <div className="flex items-center gap-2 overflow-x-auto camera-tabs-scroll pb-2" role="tablist">
                    {cameraTabs.map((camera) => (
                      <button
                        key={camera}
                        onClick={() => handleDragDropCameraChange(camera as CameraId)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                          dragDropCamera === camera
                            ? 'bg-[#0C5484] text-white'
                            : 'bg-[#161513] text-gray-300 hover:bg-[#161513]/80 border border-[#0CB4E4]/30'
                        }`}
                        role="tab"
                        aria-selected={dragDropCamera === camera}
                      >
                         <Camera className="w-4 h-4" />
                        <span className="font-medium">
                          {getCameraLabel(camera)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {dragDropCamera !== 'all' && (
            <div
              className="border-2 border-dashed rounded-lg p-8 border-[#0CB4E4]/30"
            >
              <div className="relative w-16 h-16 mx-auto mb-4 flex justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0CB4E4]/20 to-[#0CB4E4]/30 blur-xl rounded-full animate-pulse" />
                <div className="relative bg-[#161513]/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-[#0CB4E4]/30">
                  <ImageUpload className="w-8 h-8 text-[#0CB4E4]" strokeWidth={1.5} />
                </div>
              </div>


              
              <p className="text-xl font-semibold text-white mb-2 text-center">
                {getDragDropText()}
              </p>
              <div className="flex justify-center mt-4">
                <label className="bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-white px-8 py-3 rounded-xl cursor-pointer hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 transition-all duration-300 shadow-lg font-medium inline-block">
                Browse Files
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/heic,.exr"
                  onChange={handleFileSelect}
                />
                </label>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="p-4 bg-[#0CB4E4]/10 rounded-lg border border-[#0CB4E4]/30">
                  <h3 className="font-medium text-white mb-2">Supported Formats</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• JPG, JPEG (recommended), PNG, EXR </li>
                    <li>• HEIC (iOS photos - auto-converted to JPEG)</li>
                  </ul>
                </div>
                <div className="p-4 bg-[#0CB4E4]/10 rounded-lg border border-[#0CB4E4]/30">
                  <h3 className="font-medium text-white mb-2">Requirements</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• Landscape orientation only</li>
                    <li>• Consistent resolution per camera</li>
                    <li>• Max size: 15MB (JPEG), 25MB (PNG), 40MB (EXR)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {dragDropCamera === 'all' && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center bg-[#0CB4E4]/10 border-[#0CB4E4]/30">
              <div className="p-4 bg-[#0CB4E4]/20 rounded-lg border border-[#0CB4E4]/30 max-w-2xl mx-auto">
                <h3 className="font-medium text-[#0CB4E4] mb-2">Cannot Upload to All Cameras</h3>
                <p className="text-sm text-gray-300">
                  Please select a specific camera tab above to upload images. Images need to be associated with a specific camera for proper reconstruction.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {images.length > 0 && (
        <div className="mt-8 relative z-10">
          {/* Validation Dashboard - Replaces Quality Controls */}
          <ValidationDashboard />

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {cameraTabs.length > 1 && selectedCamera !== 'all'
                ? `${getCameraLabel(selectedCamera)} Images`
                : selectedCamera === 'all'
                  ? 'All Camera Images'
                  : 'Scene Images'}
            </h2>
            
          </div>

          <div className="p-4 bg-[#161513] rounded-lg border border-[#0CB4E4]/30 mb-6 shadow-md">
            <div className="text-sm font-medium text-white mb-2">View Images From</div>
            <div className="flex items-center gap-2 overflow-x-auto camera-tabs-scroll pb-2" role="tablist">
              {cameraOptions.map((camera) => (
                <button 
                  key={camera}
                  onClick={() => handleDragDropCameraChange(camera as CameraId)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                    selectedCamera === camera
                      ? 'bg-[#0CB4E4] text-[#161513]'
                      : 'bg-[#161513] text-gray-300 hover:bg-[#161513]/80 border border-[#0CB4E4]/30'
                  }`}
                  role="tab"
                  aria-selected={selectedCamera === camera}
                >
                  <Camera className="w-4 h-4" />
                  <span className="font-medium">
                    {getCameraLabel(camera)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {cameraImages.length === 0 ? (
            <div className="p-12 border-2 border-dashed border-[#0CB4E4]/30 rounded-lg text-center bg-[#161513]/50">
              <ImageIcon className="w-12 h-12 text-[#0CB4E4]/50 mx-auto mb-4" />
              <p className="text-lg font-medium text-white mb-2">
                {selectedCamera === 'all'
                  ? 'No images uploaded yet'
                  : `No images uploaded for ${getCameraLabel(selectedCamera)}`}
              </p>
              <p className="text-sm text-gray-300 mb-6">Upload images to begin the reconstruction process</p>
              <label className="px-4 py-2 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/80 rounded-lg font-medium cursor-pointer inline-block shadow-md">
                Upload Images
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/heic,.exr"
                  onChange={handleFileSelect}
                />
              </label>
            </div>
          ) : (
            <>
            {!isExrDataset ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {paginatedImages.map((image) => (
                  <div key={image.id} className={`relative group ${
                    image.validationErrors && image.validationErrors.length > 0 ? 'ring-2 ring-[#0CB4E4]' : ''
                  }`}>
                    <button
                      className={`absolute top-2 left-2 p-2 transition-all duration-200 ${
                        selectedImages.has(image.id)
                          ? 'bg-[#0C5484] text-white'
                          : 'bg-black/50 text-white hover:bg-black/70'
                      } backdrop-blur-sm rounded-lg ${selectedImages.has(image.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} z-10`}
                      onClick={(e) => toggleImageSelection(image.id, e)}
                    >
                      <div className="w-4 h-4 border-2 rounded-sm transition-colors relative">
                        {selectedImages.has(image.id) && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            ✓
                          </div>
                        )}
                      </div>
                    </button>
                    <div
                      className={`w-full text-left focus:outline-none rounded-lg transition-all duration-300 ${
                        selectedImages.has(image.id) ? 'ring-4 ring-[#0CB4E4] ring-opacity-50' : ''
                      }`}
                    >
                      <div className="relative overflow-hidden rounded-xl shadow-lg">
                        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/30 group-hover:opacity-0 transition-opacity duration-300" />
                        <img
                          src={image.preview}
                          alt="Preview"
                          className="w-full h-48 object-cover transition-all duration-500 group-hover:scale-[1.03]"
                        />
                        {image.cameraId && cameraTabs.length > 1 && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full">
                            {image.cameraId.replace('camera', 'Cam ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute top-2 right-2 p-2 bg-[#808080] text-white hover:bg-[#808080]/80 rounded-lg transition-all shadow-sm opacity-0 group-hover:opacity-100"
                      title="Remove image"
                      aria-label="Remove image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    
                    {/* Real Validation Status Overlay */}
                    {image.validationErrors && image.validationErrors.length > 0 ? (
                      <div className="absolute bottom-2 left-2 right-2 bg-[#0CB4E4]/90 backdrop-blur-xl text-white text-xs py-2 px-3 rounded-xl shadow-xl border border-white/10">
                        ❌ {image.validationErrors[0].rule.replace('-', ' ').toUpperCase()}
                      </div>
                    ) : validationState.canReconstruct && validationState.status === 'complete' ? (
                      <div className="absolute bottom-2 left-2 right-2 bg-green-500/90 backdrop-blur-xl text-white text-xs py-2 px-3 rounded-xl shadow-xl border border-white/10">
                        ✅ Validated
                      </div>
                    ) : validationState.status === 'validating' ? (
                      <div className="absolute bottom-2 left-2 right-2 bg-[#0CB4E4]/90 backdrop-blur-xl text-white text-xs py-2 px-3 rounded-xl shadow-xl border border-white/10">
                        🔄 Checking...
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              ) : (
              <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      EXR Dataset Loaded
                    </h3>
                    <p className="text-gray-300 text-sm mb-4">
                      High dynamic range image sequence detected. Preview not available for EXR format.
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-400 min-w-[80px]">Total Files:</span>
                        <span className="text-white font-medium">{cameraImages.length} images</span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-400 min-w-[80px]">Format:</span>
                        <span className="text-purple-400 font-medium">EXR (OpenEXR)</span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-400 min-w-[80px]">Total Size:</span>
                        <span className="text-white font-medium">
                          {(cameraImages.reduce((acc, img) => acc + (img.file.size || 0), 0) / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      </div>
                    </div>

                    <details className="mt-4 group">
                      <summary className="cursor-pointer text-sm text-[#0CB4E4] hover:text-[#0CB4E4]/80 flex items-center gap-2">
                        <span>View file list</span>
                        <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </summary>
                      
                      <div className="mt-3 max-h-48 overflow-y-auto bg-[#161513] rounded-lg p-3">
                        <ul className="space-y-1 text-xs font-mono text-gray-400">
                          {cameraImages.map((image, idx) => (
                            <li key={image.id} className="flex items-center justify-between py-1 border-b border-gray-700/30 last:border-0">
                              <span className="truncate flex-1 mr-2">{idx + 1}. {image.file.name}</span>
                              <span className="text-gray-500 flex-shrink-0">
                                {((image.file.size || 0) / 1024).toFixed(0)} KB
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            )}
              {/* Delete Confirmation Dialog */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
                  <div className="bg-[#161513] rounded-xl shadow-xl w-full max-w-md p-6 border border-[#0CB4E4]/30">
                    <h3 className="text-lg font-semibold text-white mb-2">Delete All Images?</h3>
                    <p className="text-gray-300 mb-6">
                      Are you sure you want to delete all {cameraImages.length} images? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 text-gray-300 hover:text-white font-medium rounded-lg hover:bg-[#161513] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          onImagesSelected(images.filter(img => img.cameraId !== selectedCamera));
                          setShowDeleteConfirm(false);
                        }}
                        className="px-4 py-2 bg-[#0CB4E4]/70 text-white font-medium rounded-lg hover:bg-[#0CB4E4]/90 transition-colors shadow-md"
                      >
                        Delete All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-300">
                    Showing {startIndex + 1}-{Math.min(startIndex + imagesPerPage, cameraImages.length)} of {cameraImages.length} images
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1 rounded-lg hover:bg-[#161513] disabled:opacity-50 disabled:cursor-not-allowed text-white"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm font-medium text-white">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 rounded-lg hover:bg-[#161513] disabled:opacity-50 disabled:cursor-not-allowed text-white"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      
   
     <div className="mt-4 bg-[#0CB4E4]/10 p-4 rounded-lg border border-[#0CB4E4]/30 flex items-start gap-3">
  <div>
    <h4 className="font-semibold text-white">Scan using your iPhone</h4>
    <p className="text-sm text-gray-400 mt-1">
      <a 
        href="https://apps.apple.com/app/id6751804720 " 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-[#0CB4E4] hover:underline"
      >
        Download
      </a>
      {' '}our CR3ATE app. It's the easiest way to get started.
    </p>
  </div>
 
</div>

    </div>
    
  );
}
